import Link from 'next/link';
import { useRef } from 'react';

import {
    dispatchEvent,
    EventKey,
    StoryBoxCTAAnalyticsData,
    StoryBoxLinkAnalyticsData,
} from '@tgg/services';

import { Heading } from '../Heading';

import { STORY_BOX_SIZES, height, width } from './StoryBox.constants';
import {
    ParagraphWrapper,
    StyledButton,
    StyledButtonContainer,
    StyledContent,
    StyledImageStandalone,
    StyledPill,
    StyledRichTextContent,
    StyledTagItem,
    StyledTagLink,
    StyledTagsList,
    StyledWrapper,
} from './StoryBox.styled';
import { StoryBoxProperties } from './StoryBox.types';

const handleCTAClick = (
    title: string,
    ctaDestinationUrl: string,
    text: string,
    position: string,
) =>
    dispatchEvent<StoryBoxCTAAnalyticsData>(EventKey.CTA_BUTTON, {
        event: EventKey.CTA_BUTTON,
        component_name: 'story box',
        content_title: title,
        cta_destination: ctaDestinationUrl,
        cta_text: text,
        cta_position: position,
    });

export const handleLinkClick = (event: Event) => {
    if (
        event.target &&
        event.target instanceof HTMLAnchorElement &&
        event.target.tagName === 'A'
    ) {
        const linkHref = event.target.getAttribute('href') as string;
        const url = linkHref.startsWith('http')
            ? linkHref
            : new URL(linkHref, window.location.origin).pathname;
        const linkText = (event.target.textContent as string).trim();
        dispatchEvent<StoryBoxLinkAnalyticsData>(EventKey.LINK_CLICK, {
            event: EventKey.LINK_CLICK,
            component_name: 'story box',
            link_destination: url as string,
            link_text: linkText,
        });
    }
};

export const StoryBox = ({
    button,
    secondaryButton,
    image,
    paragraph,
    pill,
    size,
    tags,
    title,
    variant,
}: StoryBoxProperties) => {
    const descriptionReference = useRef<HTMLElement>(null);

    const buttonStyle = variant === 'light blue' ? 'primary' : 'secondary';

    return (
        <StyledWrapper $variant={variant} data-testid="story-box-wrapper">
            {image?.image && (
                <StyledImageStandalone
                    {...{
                        ...image,
                        height,
                        width,
                        overrideSizes: STORY_BOX_SIZES,
                    }}
                    $size={size}
                />
            )}
            <StyledContent>
                {tags && (
                    <StyledTagsList>
                        {tags?.map(tag => (
                            <StyledTagItem key={tag.href}>
                                <StyledTagLink href={tag.href}>
                                    {tag.text}
                                </StyledTagLink>
                            </StyledTagItem>
                        ))}
                    </StyledTagsList>
                )}
                <Heading variant="h3">{title}</Heading>
                <ParagraphWrapper
                    ref={descriptionReference}
                    onClick={handleLinkClick}
                >
                    <StyledRichTextContent text={paragraph} fullWidth />
                </ParagraphWrapper>
                <StyledButtonContainer>
                    <Link href={button.href} legacyBehavior>
                        <StyledButton
                            text={button.text}
                            href={!button.isUrlHidden ? button.href : ''}
                            buttonStyle={buttonStyle}
                            onClick={() => {
                                handleCTAClick(
                                    title,
                                    button.href,
                                    button.text,
                                    `${secondaryButton ? '1/2' : '1/1'}`,
                                );
                            }}
                        />
                    </Link>
                    {size === 'large' && secondaryButton && (
                        <Link href={secondaryButton.href} legacyBehavior>
                            <StyledButton
                                text={secondaryButton.text}
                                href={!button.isUrlHidden ? button.href : ''}
                                buttonStyle={buttonStyle}
                                onClick={() => {
                                    handleCTAClick(
                                        title,
                                        secondaryButton.href,
                                        secondaryButton.text,
                                        '2/2',
                                    );
                                }}
                            />
                        </Link>
                    )}
                </StyledButtonContainer>
                {pill && <StyledPill>{pill}</StyledPill>}
            </StyledContent>
        </StyledWrapper>
    );
};
